import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';

interface SearchSuggestion {
  id: string;
  title: string;
  type: string;
  snippet?: string;
}

interface SearchBarProps {
  onSearch?: (query: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  placeholder = '搜索...',
  autoFocus = false,
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<number | null>(null);

  // Load search history from localStorage
  useEffect(() => {
    const history = localStorage.getItem('searchHistory');
    if (history) {
      setSearchHistory(JSON.parse(history));
    }
  }, []);

  // Handle keyboard shortcuts (Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        inputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        !inputRef.current?.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions with debounce
  const fetchSuggestions = async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const result = await apiClient.request<{ results: SearchSuggestion[] }>(
        `/api/search?q=${encodeURIComponent(searchQuery)}&limit=5`
      );

      if (result.data) {
        setSuggestions(result.data.results);
      }
    } catch (error) {
      console.error('Search suggestions error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setShowSuggestions(true);

    // Debounce search
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  const handleSearch = (searchQuery: string) => {
    if (!searchQuery.trim()) return;

    // Add to search history
    const newHistory = [
      searchQuery,
      ...searchHistory.filter(h => h !== searchQuery),
    ].slice(0, 10);
    setSearchHistory(newHistory);
    localStorage.setItem('searchHistory', JSON.stringify(newHistory));

    // Perform search
    if (onSearch) {
      onSearch(searchQuery);
    } else {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }

    setShowSuggestions(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(query);
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    setQuery(suggestion.title);
    handleSearch(suggestion.title);
  };

  const handleHistoryClick = (historyItem: string) => {
    setQuery(historyItem);
    handleSearch(historyItem);
  };

  const clearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('searchHistory');
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'bookmark': return '🔖';
      case 'note': return '📝';
      case 'expense': return '💰';
      case 'schedule': return '📅';
      case 'reminder': return '⏰';
      default: return '📄';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'bookmark': return '书签';
      case 'note': return '笔记';
      case 'expense': return '记账';
      case 'schedule': return '日程';
      case 'reminder': return '提醒';
      default: return '内容';
    }
  };

  return (
    <div className="search-bar">
      <form onSubmit={handleSubmit} className="search-form">
        <span className="search-icon">🔍</span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className="search-input"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              setSuggestions([]);
              inputRef.current?.focus();
            }}
            className="clear-btn"
          >
            ×
          </button>
        )}
        <kbd className="search-shortcut">Ctrl+K</kbd>
      </form>

      {showSuggestions && (
        <div ref={suggestionsRef} className="suggestions-dropdown">
          {isLoading ? (
            <div className="suggestions-loading">搜索中...</div>
          ) : query.trim() ? (
            suggestions.length > 0 ? (
              <>
                <div className="suggestions-section">
                  <div className="suggestions-title">搜索结果</div>
                  {suggestions.map(suggestion => (
                    <button
                      key={suggestion.id}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="suggestion-item"
                    >
                      <span className="suggestion-icon">
                        {getTypeIcon(suggestion.type)}
                      </span>
                      <div className="suggestion-content">
                        <div className="suggestion-title">{suggestion.title}</div>
                        {suggestion.snippet && (
                          <div className="suggestion-snippet">{suggestion.snippet}</div>
                        )}
                      </div>
                      <span className="suggestion-type">
                        {getTypeLabel(suggestion.type)}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="suggestions-footer">
                  <button onClick={() => handleSearch(query)} className="view-all-btn">
                    查看全部结果 →
                  </button>
                </div>
              </>
            ) : (
              <div className="suggestions-empty">未找到相关内容</div>
            )
          ) : searchHistory.length > 0 ? (
            <div className="suggestions-section">
              <div className="suggestions-header">
                <div className="suggestions-title">搜索历史</div>
                <button onClick={clearHistory} className="clear-history-btn">
                  清除
                </button>
              </div>
              {searchHistory.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleHistoryClick(item)}
                  className="suggestion-item history-item"
                >
                  <span className="suggestion-icon">🕐</span>
                  <div className="suggestion-content">
                    <div className="suggestion-title">{item}</div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="suggestions-empty">输入关键词开始搜索</div>
          )}
        </div>
      )}

      <style>{`
        .search-bar {
          position: relative;
          width: 100%;
          max-width: 600px;
        }

        .search-form {
          position: relative;
          display: flex;
          align-items: center;
          background: white;
          border: 2px solid #e0e0e0;
          border-radius: 24px;
          padding: 8px 16px;
          transition: all 0.2s;
        }

        .search-form:focus-within {
          border-color: #007bff;
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
        }

        .search-icon {
          font-size: 18px;
          margin-right: 8px;
          color: #666;
        }

        .search-input {
          flex: 1;
          border: none;
          outline: none;
          font-size: 15px;
          padding: 4px 0;
          background: transparent;
        }

        .clear-btn {
          background: none;
          border: none;
          font-size: 24px;
          color: #999;
          cursor: pointer;
          padding: 0;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.2s;
        }

        .clear-btn:hover {
          background: #f0f0f0;
          color: #666;
        }

        .search-shortcut {
          margin-left: 8px;
          padding: 2px 8px;
          background: #f5f5f5;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          font-size: 11px;
          color: #666;
          font-family: monospace;
        }

        .suggestions-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          right: 0;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          max-height: 400px;
          overflow-y: auto;
          z-index: 1000;
        }

        .suggestions-section {
          padding: 8px 0;
        }

        .suggestions-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 16px;
        }

        .suggestions-title {
          font-size: 12px;
          font-weight: 600;
          color: #999;
          text-transform: uppercase;
          padding: 8px 16px;
        }

        .clear-history-btn {
          background: none;
          border: none;
          color: #007bff;
          font-size: 12px;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 4px;
          transition: background 0.2s;
        }

        .clear-history-btn:hover {
          background: #f0f8ff;
        }

        .suggestion-item {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 12px 16px;
          border: none;
          background: none;
          text-align: left;
          cursor: pointer;
          transition: background 0.2s;
        }

        .suggestion-item:hover {
          background: #f5f5f5;
        }

        .suggestion-icon {
          font-size: 20px;
          flex-shrink: 0;
        }

        .suggestion-content {
          flex: 1;
          min-width: 0;
        }

        .suggestion-title {
          font-size: 14px;
          font-weight: 500;
          color: #333;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .suggestion-snippet {
          font-size: 12px;
          color: #666;
          margin-top: 2px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .suggestion-type {
          font-size: 11px;
          color: #999;
          padding: 2px 8px;
          background: #f0f0f0;
          border-radius: 10px;
          flex-shrink: 0;
        }

        .suggestions-footer {
          padding: 8px;
          border-top: 1px solid #f0f0f0;
        }

        .view-all-btn {
          width: 100%;
          padding: 8px;
          background: none;
          border: none;
          color: #007bff;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          border-radius: 6px;
          transition: background 0.2s;
        }

        .view-all-btn:hover {
          background: #f0f8ff;
        }

        .suggestions-loading,
        .suggestions-empty {
          text-align: center;
          padding: 24px;
          color: #999;
          font-size: 14px;
        }

        @media (max-width: 768px) {
          .search-bar {
            max-width: 100%;
          }

          .search-shortcut {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};
